import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Tabs,
  Table,
  Descriptions,
  Tag,
  Button,
  Spin,
  message,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Popconfirm,
  Segmented,
  Card,
  Image,
  Avatar,
  Typography,
  Upload,
} from 'antd';
import type { UploadProps, RcFile } from 'antd/es/upload';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import {
  getOrganization,
  getOrganizations,
  getUsers,
  getAssets,
  getSims,
  createUser,
  updateUser,
  deleteUser,
  createAsset,
  deleteAsset,
  moveAssetToOrganization,
  allocateSim,
  deallocateSim,
  getOrgLogoUploadUrl,
  setOrgLogo,
  removeOrgLogo,
  Organization,
  User,
  Asset,
  UnifiedSim,
} from '../../services/adminService';
import { useAuth } from '../../auth/AuthContext';
import LookupSelect from '../../components/LookupSelect';
import PageHelp from '../../components/PageHelp';
import UserForm from '../../components/UserForm';
import { providerLabel } from '../../utils/providerLabel';

const OrganizationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role: myRole } = useAuth();
  const canManage =
    myRole === 'super_admin' || myRole === 'reseller_admin' || myRole === 'customer_admin';
  // KAN-85: org logo management mirrors vessel logos — super_admin / reseller_admin only.
  const canManageLogo = myRole === 'super_admin' || myRole === 'reseller_admin';

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [sims, setSims] = useState<UnifiedSim[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [simsLoading, setSimsLoading] = useState(false);

  const [addUserVisible, setAddUserVisible] = useState(false);
  const [addUserMode, setAddUserMode] = useState<'create' | 'existing'>('create');
  const [moveUserId, setMoveUserId] = useState<number | null>(null);
  // Move-existing-user modal (initiated from a user row's 'Move to…' button)
  const [moveOutModalUser, setMoveOutModalUser] = useState<User | null>(null);
  const [moveOutTargetOrg, setMoveOutTargetOrg] = useState<number | null>(null);
  const [moveOutSubmitting, setMoveOutSubmitting] = useState(false);
  const [addAssetVisible, setAddAssetVisible] = useState(false);
  const [addAssetMode, setAddAssetMode] = useState<'create' | 'existing'>('create');
  const [moveAssetId, setMoveAssetId] = useState<number | null>(null);
  const [allocateSimVisible, setAllocateSimVisible] = useState(false);
  const [unallocatedSims, setUnallocatedSims] = useState<UnifiedSim[]>([]);
  const [allUsersList, setAllUsersList] = useState<User[]>([]);
  const [allAssetsList, setAllAssetsList] = useState<Asset[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [simSearchLoading, setSimSearchLoading] = useState(false);
  const simSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Selected SIMs persist across searches (keyed by provider:identifier) so a
  // user can search one alias, pick a few, search another, and pick more.
  const [allocateSelectedSims, setAllocateSelectedSims] = useState<Record<string, UnifiedSim>>({});
  const selectedAllocateCount = Object.keys(allocateSelectedSims).length;

  const [addUserForm] = Form.useForm();
  const [addAssetForm] = Form.useForm();
  const [allocateSimForm] = Form.useForm();

  const orgId = Number(id);

  // KAN-85: organization logo upload state.
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoRemoving, setLogoRemoving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [org, orgs] = await Promise.all([getOrganization(orgId), getOrganizations()]);
        setOrganization(org);
        setOrganizations(orgs);
      } catch (err: any) {
        message.error(err.message || 'Failed to load organization');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId]);

  // KAN-85: organization logo handlers (mirror the vessel logo flow in AssetDetail).
  const refreshOrg = async () => {
    try {
      const org = await getOrganization(orgId);
      setOrganization(org);
    } catch (err) {
      console.warn('Failed to refresh organization after logo change', err);
    }
  };

  const validateLogoFile = (file: RcFile): boolean => {
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      message.error('Logo must be a PNG, JPEG, or WebP image.');
      return false;
    }
    if (file.size > 2 * 1024 * 1024) {
      message.error('Logo must be 2MB or smaller.');
      return false;
    }
    return true;
  };

  const handleLogoUpload: UploadProps['customRequest'] = async options => {
    const { file, onSuccess, onError } = options;
    const rcFile = file as RcFile;
    if (!validateLogoFile(rcFile)) {
      const err = new Error('Invalid logo file');
      onError?.(err);
      return;
    }
    setLogoUploading(true);
    try {
      // 1. Ask our API for a presigned PUT URL.
      const { uploadUrl, s3Key } = await getOrgLogoUploadUrl(orgId, rcFile.type);
      // 2. Upload the raw bytes directly to S3 (NOT our API — raw fetch is correct).
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': rcFile.type },
        body: rcFile,
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`);
      }
      // 3. Tell our API the upload is committed.
      await setOrgLogo(orgId, s3Key);
      message.success('Organization logo updated.');
      await refreshOrg();
      onSuccess?.({}, new XMLHttpRequest());
    } catch (err) {
      console.warn('Logo upload failed', err);
      message.error('Failed to upload organization logo. Please try again.');
      onError?.(err instanceof Error ? err : new Error('Logo upload failed'));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoRemoving(true);
    try {
      await removeOrgLogo(orgId);
      message.success('Organization logo removed.');
      await refreshOrg();
    } catch (err) {
      console.warn('Logo removal failed', err);
      message.error('Failed to remove organization logo. Please try again.');
    } finally {
      setLogoRemoving(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const allUsers = await getUsers();
      setUsers(allUsers.filter(u => u.organization_id === orgId));
    } catch {
      /* ignore */
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchAssets = async () => {
    setAssetsLoading(true);
    try {
      const allAssets = await getAssets();
      const childOrgIds = organizations.filter(o => o.parent_org_id === orgId).map(o => o.id);
      const scopeIds = new Set([orgId, ...childOrgIds]);
      setAssets(allAssets.filter(a => scopeIds.has(a.organization_id)));
    } catch {
      /* ignore */
    } finally {
      setAssetsLoading(false);
    }
  };

  const fetchSims = async () => {
    setSimsLoading(true);
    try {
      const allSims = await getSims();
      const childOrgIds = organizations.filter(o => o.parent_org_id === orgId).map(o => o.id);
      const scopeIds = new Set([orgId, ...childOrgIds]);
      setSims(allSims.filter(s => s.allocation && scopeIds.has(s.allocation.organizationId)));
    } catch {
      /* ignore */
    } finally {
      setSimsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [orgId]);

  useEffect(() => {
    if (organizations.length > 0) {
      fetchAssets();
      fetchSims();
    }
    // eslint-disable-next-line
  }, [orgId, organizations]);

  // Handlers
  const openAddUser = async () => {
    setAddUserMode('create');
    setMoveUserId(null);
    addUserForm.resetFields();
    try {
      const all = await getUsers();
      // Existing-mode candidates: active users currently in a *different* org
      setAllUsersList(all.filter(u => u.is_active && u.organization_id !== orgId));
    } catch {
      setAllUsersList([]);
    }
    setAddUserVisible(true);
  };

  const handleAddUser = async (values: any) => {
    setSubmitting(true);
    try {
      await createUser({ ...values, organizationId: orgId });
      message.success('User created');
      setAddUserVisible(false);
      addUserForm.resetFields();
      fetchUsers();
    } catch (err: any) {
      message.error(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoveExistingUser = async () => {
    if (!moveUserId) return;
    setSubmitting(true);
    try {
      await updateUser(moveUserId, { organizationId: orgId });
      message.success('User moved to this organization');
      setAddUserVisible(false);
      fetchUsers();
    } catch (err: any) {
      message.error(err.message || 'Failed to move user');
    } finally {
      setSubmitting(false);
    }
  };

  const openMoveUserModal = (u: User) => {
    setMoveOutModalUser(u);
    setMoveOutTargetOrg(null);
  };

  const handleMoveUserOut = async () => {
    if (!moveOutModalUser || !moveOutTargetOrg) return;
    setMoveOutSubmitting(true);
    try {
      await updateUser(moveOutModalUser.id, { organizationId: moveOutTargetOrg });
      message.success(
        `Moved ${moveOutModalUser.first_name} ${moveOutModalUser.last_name} to another organization`
      );
      setMoveOutModalUser(null);
      fetchUsers();
    } catch (err: any) {
      message.error(err.message || 'Failed to move user');
    } finally {
      setMoveOutSubmitting(false);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    try {
      await deleteUser(userId);
      message.success('User deactivated');
      fetchUsers();
    } catch (err: any) {
      message.error(err.message || 'Failed to remove user');
    }
  };

  const openAddAsset = async () => {
    setAddAssetMode('create');
    setMoveAssetId(null);
    addAssetForm.resetFields();
    try {
      const all = await getAssets();
      // Existing-mode candidates: active assets in a *different* org
      setAllAssetsList(all.filter(a => a.is_active && a.organization_id !== orgId));
    } catch {
      setAllAssetsList([]);
    }
    setAddAssetVisible(true);
  };

  const handleAddAsset = async (values: any) => {
    setSubmitting(true);
    try {
      await createAsset({ ...values, organizationId: orgId });
      message.success('Asset created');
      setAddAssetVisible(false);
      addAssetForm.resetFields();
      fetchAssets();
    } catch (err: any) {
      message.error(err.message || 'Failed to create asset');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoveExistingAsset = async () => {
    if (!moveAssetId) return;
    setSubmitting(true);
    try {
      const result = await moveAssetToOrganization(moveAssetId, orgId);
      message.success(
        `Asset moved (${result.moved_sims} SIM(s), ${result.moved_users} user(s) also moved)`
      );
      setAddAssetVisible(false);
      fetchAssets();
    } catch (err: any) {
      message.error(err.message || 'Failed to move asset');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAsset = async (assetId: number) => {
    try {
      await deleteAsset(assetId);
      message.success('Asset deactivated');
      fetchAssets();
    } catch (err: any) {
      message.error(err.message || 'Failed to remove asset');
    }
  };

  // Server-side SIM search. The backend matches ICCID/MSISDN/alias across all
  // available SIMs; client-side filtering only saw the first slice of options,
  // so aliases beyond that slice (e.g. a customer with thousands of SIMs) never
  // surfaced. Filter out already-allocated SIMs from the result.
  const loadUnallocatedSims = async (search?: string) => {
    setSimSearchLoading(true);
    try {
      const allSims = await getSims(search ? { search } : undefined);
      setUnallocatedSims(allSims.filter(s => !s.allocation));
    } catch {
      setUnallocatedSims([]);
    } finally {
      setSimSearchLoading(false);
    }
  };

  const handleSimSearch = (value: string) => {
    if (simSearchRef.current) clearTimeout(simSearchRef.current);
    simSearchRef.current = setTimeout(() => {
      void loadUnallocatedSims(value.trim() || undefined);
    }, 300);
  };

  const openAllocateSim = async () => {
    allocateSimForm.resetFields();
    setAllocateSelectedSims({});
    setAllocateSimVisible(true);
    await loadUnallocatedSims();
  };

  const handleAllocateSim = async () => {
    const sims = Object.values(allocateSelectedSims);
    if (sims.length === 0) return;
    setSubmitting(true);
    let successes = 0;
    const errors: string[] = [];
    for (const sim of sims) {
      try {
        await allocateSim({
          identifier: sim.identifier,
          provider: sim.provider,
          organizationId: orgId,
        });
        successes++;
      } catch (err: any) {
        errors.push(`${sim.identifier}: ${err.message || 'Failed'}`);
      }
    }
    setSubmitting(false);
    if (successes > 0) {
      message.success(`Allocated ${successes} of ${sims.length} SIM${sims.length > 1 ? 's' : ''}`);
    }
    if (errors.length > 0) {
      message.error(`Failed to allocate ${errors.length}: ${errors.join('; ')}`);
    } else {
      setAllocateSimVisible(false);
    }
    fetchSims();
  };

  const handleDeallocateSim = async (assignmentId: number) => {
    try {
      await deallocateSim(assignmentId);
      message.success('SIM deallocated');
      fetchSims();
    } catch (err: any) {
      message.error(err.message || 'Failed to deallocate SIM');
    }
  };

  const parentName = organization?.parent_org_id
    ? organizations.find(o => o.id === organization.parent_org_id)?.name || '-'
    : '-';

  const typeColor = (type: string) => {
    switch (type) {
      case 'platform':
        return 'purple';
      case 'reseller':
        return 'blue';
      case 'customer':
        return 'green';
      default:
        return 'default';
    }
  };

  const roleColors: Record<string, string> = {
    super_admin: 'red',
    reseller_admin: 'volcano',
    customer_admin: 'blue',
    asset_manager: 'cyan',
    crew: 'default',
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div style={{ padding: 24 }}>
        <p>Organization not found.</p>
        <Button onClick={() => navigate('/admin/orgs-assets')}>Back to Org &amp; Assets</Button>
      </div>
    );
  }

  const pagination = {
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} of ${total} items`,
  };

  // Starlink terminals get their own tab; the SIM tab keeps Crew and Data SIMs.
  const simTab = (key: string, label: string, rows: UnifiedSim[], starlink: boolean) => ({
    key,
    label: `${label} (${rows.length})`,
    children: (
      <>
        {canManage && (
          <div style={{ marginBottom: 16 }}>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAllocateSim}>
              {starlink ? 'Allocate terminal' : 'Allocate SIM'}
            </Button>
          </div>
        )}
        <Table
          columns={[
            {
              title: 'Provider',
              key: 'provider',
              render: (_: any, r: UnifiedSim) => (
                <Tag color={r.provider === 'vrs' ? 'blue' : 'green'}>
                  {providerLabel(r.provider)}
                </Tag>
              ),
            },
            {
              title: 'Identifier',
              key: 'identifier',
              render: (_: any, r: UnifiedSim) =>
                r.provider === 'vrs' ? (
                  <Link to={`/admin/sims/${r.provider}/${r.identifier}`}>{r.identifier}</Link>
                ) : r.provider === 'clarus' ? (
                  <Link to={`/admin/terminals/${r.identifier}`}>{r.identifier}</Link>
                ) : (
                  r.identifier
                ),
            },
            ...(starlink
              ? []
              : [
                  {
                    title: 'MSISDN',
                    key: 'msisdn',
                    render: (_: any, r: UnifiedSim) => r.msisdn || '-',
                  },
                ]),
            {
              title: 'Alias',
              key: 'alias',
              render: (_: any, r: UnifiedSim) => r.alias || '-',
            },
            {
              title: 'Status',
              key: 'status',
              render: (_: any, r: UnifiedSim) => (
                <Tag
                  color={
                    r.status === 'active'
                      ? 'green'
                      : r.status === 'suspended'
                        ? 'orange'
                        : 'default'
                  }
                >
                  {r.status}
                </Tag>
              ),
            },
            {
              title: 'Assigned To',
              key: 'assignedTo',
              render: (_: any, r: UnifiedSim) => {
                if (!r.allocation) return '-';
                const parts: React.ReactNode[] = [];
                if (r.allocation.assetName) {
                  parts.push(
                    <Link key="a" to={`/admin/assets/${r.allocation.assetId}`}>
                      {r.allocation.assetName}
                    </Link>
                  );
                }
                if (r.allocation.userName) {
                  parts.push(
                    <span key="u">
                      {parts.length > 0 ? ' / ' : ''}
                      <Link to={`/admin/users/${r.allocation.userId}`}>
                        {r.allocation.userName}
                      </Link>
                    </span>
                  );
                }
                if (r.allocation.simType) {
                  parts.push(
                    <Tag key="t" color="purple" style={{ marginLeft: 4 }}>
                      {r.allocation.simType}
                    </Tag>
                  );
                }
                return parts.length > 0 ? <>{parts}</> : '-';
              },
            },
            ...(canManage
              ? [
                  {
                    title: 'Actions',
                    key: 'actions',
                    render: (_: any, r: UnifiedSim) =>
                      r.allocation?.organizationId === orgId ? (
                        <Popconfirm
                          title="Deallocate this SIM from organization?"
                          onConfirm={() => handleDeallocateSim(r.allocation!.assignmentId)}
                        >
                          <Button type="link" danger size="small">
                            Deallocate
                          </Button>
                        </Popconfirm>
                      ) : (
                        <span style={{ color: '#999', fontSize: 12 }}>child org</span>
                      ),
                  },
                ]
              : []),
          ]}
          dataSource={rows}
          rowKey={(r: UnifiedSim) => `${r.provider}:${r.identifier}`}
          loading={simsLoading}
          pagination={pagination}
        />
      </>
    ),
  });

  const tabItems = [
    {
      key: 'users',
      label: `Users (${users.length})`,
      children: (
        <>
          {canManage && (
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddUser}>
                Add User
              </Button>
            </div>
          )}
          <Table
            columns={[
              {
                title: 'Name',
                key: 'name',
                render: (_: any, r: User) => (
                  <Link to={`/admin/users/${r.id}`}>
                    {r.first_name} {r.last_name}
                  </Link>
                ),
              },
              { title: 'Email', dataIndex: 'email', key: 'email' },
              {
                title: 'Role',
                dataIndex: 'role',
                key: 'role',
                render: (role: string) => <Tag color={roleColors[role] || 'default'}>{role}</Tag>,
              },
              {
                title: 'Status',
                dataIndex: 'is_active',
                key: 'is_active',
                render: (active: boolean) => (
                  <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>
                ),
              },
              ...(canManage
                ? [
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_: any, r: User) => (
                        <Space>
                          <Button
                            type="link"
                            size="small"
                            onClick={() => openMoveUserModal(r)}
                            title="Move this user to another organization (they stay active)"
                          >
                            Move to…
                          </Button>
                          <Popconfirm
                            title="Deactivate this user?"
                            description="They lose access immediately. You can reactivate them later from the Users page (use 'Show inactive')."
                            onConfirm={() => handleRemoveUser(r.id)}
                            okText="Deactivate"
                            cancelText="Cancel"
                          >
                            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                              Deactivate
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
            dataSource={users}
            rowKey="id"
            loading={usersLoading}
            pagination={pagination}
          />
        </>
      ),
    },
    {
      key: 'assets',
      label: `Assets (${assets.length})`,
      children: (
        <>
          {canManage && organization.type === 'customer' && (
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddAsset}>
                Add Asset
              </Button>
            </div>
          )}
          <Table
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                render: (name: string, record: Asset) => (
                  <Link to={`/admin/assets/${record.id}`}>{name}</Link>
                ),
              },
              { title: 'Organization', dataIndex: 'org_name', key: 'org_name' },
              {
                title: 'Type',
                dataIndex: 'asset_type',
                key: 'asset_type',
                render: (type: string) => (type ? <Tag>{type}</Tag> : '-'),
              },
              {
                title: 'Status',
                dataIndex: 'is_active',
                key: 'is_active',
                render: (active: boolean) => (
                  <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>
                ),
              },
              ...(canManage
                ? [
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_: any, r: Asset) =>
                        r.organization_id === orgId ? (
                          <Popconfirm
                            title="Deactivate this asset?"
                            onConfirm={() => handleRemoveAsset(r.id)}
                          >
                            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                              Remove
                            </Button>
                          </Popconfirm>
                        ) : (
                          <span style={{ color: '#999', fontSize: 12 }}>child org</span>
                        ),
                    },
                  ]
                : []),
            ]}
            dataSource={assets}
            rowKey="id"
            loading={assetsLoading}
            pagination={pagination}
          />
        </>
      ),
    },
    simTab(
      'sims',
      'SIM Cards',
      sims.filter(s => s.provider !== 'clarus'),
      false
    ),
    simTab(
      'starlink',
      'Starlink',
      sims.filter(s => s.provider === 'clarus'),
      true
    ),
    {
      key: 'info',
      label: 'Info',
      children: (
        <>
          {/* KAN-85: organization / reseller logo */}
          <Card title="Organization logo" style={{ marginBottom: 24 }}>
            <Space size={24} align="start" wrap>
              {organization.logoUrl ? (
                <Image
                  src={organization.logoUrl}
                  alt={`${organization.name} logo`}
                  width={120}
                  height={120}
                  style={{ objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 8 }}
                />
              ) : (
                <Avatar
                  shape="square"
                  size={120}
                  icon={<PictureOutlined />}
                  style={{ background: '#fafafa', color: '#bfbfbf' }}
                />
              )}
              <Space direction="vertical" size={8}>
                <Typography.Text type="secondary">
                  PNG, JPEG, or WebP · up to 2&nbsp;MB.
                  <br />A <strong>square</strong> image works best — recommended at least
                  256×256&nbsp;px (e.g. 512×512). Shown at 120×120.
                  <br />
                  Used for portal branding when a vessel has no logo of its own.
                </Typography.Text>
                {canManageLogo ? (
                  <Space>
                    <Upload
                      accept="image/png,image/jpeg,image/webp"
                      showUploadList={false}
                      maxCount={1}
                      beforeUpload={validateLogoFile}
                      customRequest={handleLogoUpload}
                      disabled={logoUploading || logoRemoving}
                    >
                      <Button icon={<UploadOutlined />} loading={logoUploading}>
                        {organization.logoUrl ? 'Replace logo' : 'Upload logo'}
                      </Button>
                    </Upload>
                    {organization.logoUrl && (
                      <Popconfirm title="Remove organization logo?" onConfirm={handleRemoveLogo}>
                        <Button danger loading={logoRemoving} disabled={logoUploading}>
                          Remove
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                ) : (
                  <Typography.Text type="secondary">
                    You do not have permission to change the logo.
                  </Typography.Text>
                )}
              </Space>
            </Space>
          </Card>

          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{organization.id}</Descriptions.Item>
            <Descriptions.Item label="Name">{organization.name}</Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={typeColor(organization.type)}>{organization.type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Parent Organization">{parentName}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={organization.is_active ? 'green' : 'red'}>
                {organization.is_active ? 'Active' : 'Inactive'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {new Date(organization.created_at).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              {new Date(organization.updated_at).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/orgs-assets')}
          style={{ paddingLeft: 0 }}
        >
          Back to Org &amp; Assets
        </Button>
      </div>
      <h2 style={{ marginBottom: 16 }}>
        {organization.name} <Tag color={typeColor(organization.type)}>{organization.type}</Tag>{' '}
        <PageHelp
          title="Organization Details Help"
          sections={[
            {
              title: 'Users',
              content:
                'Manage users belonging to this organization. Click a user name to view their full profile, assigned SIM cards, and asset memberships.\n\nUse "Add User" to create a new user with a specific role. They will receive an email invitation.',
              tips: [
                'Removing a user deactivates them \u2014 they can no longer log in',
                'Users inherit access based on their role and organization',
              ],
            },
            {
              title: 'Assets',
              content:
                'Assets are yachts, vessels, or equipment owned by this organization. Click an asset name to see its SIM cards, crew, and usage data.\n\nAssets can only be added to Customer-type organizations. For resellers, assets from child customer orgs are shown.',
              tips: [
                'Add Asset only appears for Customer organizations',
                'Assets from child orgs show "child org" and cannot be deleted from here',
              ],
            },
            {
              title: 'SIM Cards',
              content:
                'View all SIM cards allocated to this organization. Use "Allocate SIM" to assign an unallocated SIM from the pool.\n\nThe "Assigned To" column shows which asset or user the SIM is linked to.',
              tips: [
                'Deallocating a SIM returns it to the unallocated pool',
                'SIMs from child orgs cannot be deallocated from the parent org',
              ],
            },
          ]}
        />
      </h2>
      <Tabs defaultActiveKey="users" items={tabItems} />

      {/* Add User Modal */}
      <Modal
        title={`Add User to ${organization.name}`}
        open={addUserVisible}
        onCancel={() => setAddUserVisible(false)}
        footer={null}
      >
        <Segmented
          block
          value={addUserMode}
          onChange={v => setAddUserMode(v as 'create' | 'existing')}
          options={[
            { label: 'Create new', value: 'create' },
            {
              label: `Add existing${allUsersList.length ? ` (${allUsersList.length})` : ''}`,
              value: 'existing',
              disabled: allUsersList.length === 0,
            },
          ]}
          style={{ marginBottom: 16 }}
        />
        {addUserMode === 'create' ? (
          <>
            <UserForm
              form={addUserForm}
              mode="create"
              organizations={organizations}
              fixedOrganizationId={orgId}
              onFinish={handleAddUser}
            />
            <div style={{ marginTop: 16 }}>
              <Space>
                <Button type="primary" loading={submitting} onClick={() => addUserForm.submit()}>
                  Create User
                </Button>
                <Button onClick={() => setAddUserVisible(false)}>Cancel</Button>
              </Space>
            </div>
          </>
        ) : (
          <Form layout="vertical">
            <Form.Item
              label="User"
              required
              tooltip="Moves the user's organization to this org. They keep their role and per-asset assignments."
            >
              <Select
                showSearch
                placeholder="Select a user from another organization"
                value={moveUserId ?? undefined}
                onChange={v => setMoveUserId(v)}
                optionFilterProp="children"
              >
                {allUsersList.map(u => (
                  <Select.Option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} &lt;{u.email}&gt; —{' '}
                    {u.org_name || `org ${u.organization_id}`} · {u.role}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Space>
              <Button
                type="primary"
                disabled={!moveUserId}
                loading={submitting}
                onClick={handleMoveExistingUser}
              >
                Move to this organization
              </Button>
              <Button onClick={() => setAddUserVisible(false)}>Cancel</Button>
            </Space>
          </Form>
        )}
      </Modal>

      {/* Add Asset Modal */}
      <Modal
        title={`Add Asset to ${organization.name}`}
        open={addAssetVisible}
        onCancel={() => setAddAssetVisible(false)}
        footer={null}
      >
        <Segmented
          block
          value={addAssetMode}
          onChange={v => setAddAssetMode(v as 'create' | 'existing')}
          options={[
            { label: 'Create new', value: 'create' },
            {
              label: `Add existing${allAssetsList.length ? ` (${allAssetsList.length})` : ''}`,
              value: 'existing',
              disabled: allAssetsList.length === 0,
            },
          ]}
          style={{ marginBottom: 16 }}
        />
        {addAssetMode === 'create' ? (
          <Form form={addAssetForm} layout="vertical" onFinish={handleAddAsset}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input placeholder="e.g. My Yacht" />
            </Form.Item>
            <Form.Item name="asset_type" label="Type">
              <LookupSelect category="asset_type" placeholder="e.g. yacht, vessel" />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  Create Asset
                </Button>
                <Button onClick={() => setAddAssetVisible(false)}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <Form layout="vertical">
            <Form.Item
              label="Asset"
              required
              tooltip="Moves the asset (and its SIMs + crew users) into this org. Yachts must move into a customer-type org; the backend enforces this."
            >
              <Select
                showSearch
                placeholder="Select an asset from another organization"
                value={moveAssetId ?? undefined}
                onChange={v => setMoveAssetId(v)}
                optionFilterProp="children"
              >
                {allAssetsList.map(a => (
                  <Select.Option key={a.id} value={a.id}>
                    {a.name} ({a.asset_type || 'asset'}) —{' '}
                    {a.org_name || `org ${a.organization_id}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Space>
              <Button
                type="primary"
                disabled={!moveAssetId}
                loading={submitting}
                onClick={handleMoveExistingAsset}
              >
                Move to this organization
              </Button>
              <Button onClick={() => setAddAssetVisible(false)}>Cancel</Button>
            </Space>
          </Form>
        )}
      </Modal>

      {/* Move-user-out Modal */}
      <Modal
        title={
          moveOutModalUser
            ? `Move ${moveOutModalUser.first_name} ${moveOutModalUser.last_name} to another organization`
            : 'Move user'
        }
        open={!!moveOutModalUser}
        onCancel={() => !moveOutSubmitting && setMoveOutModalUser(null)}
        onOk={handleMoveUserOut}
        okText="Move"
        okButtonProps={{
          disabled: !moveOutTargetOrg || moveOutTargetOrg === orgId,
          loading: moveOutSubmitting,
        }}
        cancelButtonProps={{ disabled: moveOutSubmitting }}
      >
        <p style={{ color: '#666', marginTop: 0 }}>
          The user stays active and keeps their role + visibility flags. Their organization is
          changed to the one you select below.
        </p>
        <Form layout="vertical">
          <Form.Item label="Target organization" required>
            <Select
              showSearch
              placeholder="Select organization"
              value={moveOutTargetOrg ?? undefined}
              onChange={v => setMoveOutTargetOrg(v)}
              optionFilterProp="children"
            >
              {organizations
                .filter(o => o.is_active && o.id !== orgId)
                .map(o => (
                  <Select.Option key={o.id} value={o.id}>
                    {o.name} ({o.type})
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Allocate SIM Modal */}
      <Modal
        title={`Allocate SIMs to ${organization.name}`}
        open={allocateSimVisible}
        onCancel={() => setAllocateSimVisible(false)}
        width={900}
        footer={[
          <span key="count" style={{ marginRight: 'auto', color: '#999' }}>
            {selectedAllocateCount > 0 ? `${selectedAllocateCount} selected` : ''}
          </span>,
          <Button key="cancel" onClick={() => setAllocateSimVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="allocate"
            type="primary"
            loading={submitting}
            disabled={selectedAllocateCount === 0}
            onClick={handleAllocateSim}
          >
            Allocate{selectedAllocateCount > 0 ? ` (${selectedAllocateCount})` : ''}
          </Button>,
        ]}
        styles={{ footer: { display: 'flex', alignItems: 'center' } }}
      >
        <Input
          placeholder="Search by ICCID, MSISDN, or alias..."
          allowClear
          onChange={e => handleSimSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Table
          rowKey={(r: UnifiedSim) => `${r.provider}:${r.identifier}`}
          loading={simSearchLoading}
          dataSource={unallocatedSims}
          size="small"
          scroll={{ y: 360 }}
          pagination={{ pageSize: 10, showSizeChanger: false, size: 'small' }}
          rowSelection={{
            selectedRowKeys: Object.keys(allocateSelectedSims),
            preserveSelectedRowKeys: true,
            onSelect: (record: UnifiedSim, selected: boolean) => {
              const key = `${record.provider}:${record.identifier}`;
              setAllocateSelectedSims(prev => {
                const next = { ...prev };
                if (selected) next[key] = record;
                else delete next[key];
                return next;
              });
            },
            onSelectAll: (selected: boolean, _rows: UnifiedSim[], changeRows: UnifiedSim[]) => {
              setAllocateSelectedSims(prev => {
                const next = { ...prev };
                for (const r of changeRows) {
                  const key = `${r.provider}:${r.identifier}`;
                  if (selected) next[key] = r;
                  else delete next[key];
                }
                return next;
              });
            },
          }}
          columns={[
            {
              title: 'Provider',
              key: 'provider',
              render: (_: any, r: UnifiedSim) => (
                <Tag color={r.provider === 'vrs' ? 'blue' : 'green'}>
                  {providerLabel(r.provider)}
                </Tag>
              ),
            },
            {
              title: 'Identifier',
              key: 'identifier',
              render: (_: any, r: UnifiedSim) => r.identifier,
            },
            { title: 'MSISDN', key: 'msisdn', render: (_: any, r: UnifiedSim) => r.msisdn || '-' },
            { title: 'Alias', key: 'alias', render: (_: any, r: UnifiedSim) => r.alias || '-' },
            {
              title: 'Status',
              key: 'status',
              render: (_: any, r: UnifiedSim) => (
                <Tag
                  color={
                    r.status === 'active'
                      ? 'green'
                      : r.status === 'suspended'
                        ? 'orange'
                        : 'default'
                  }
                >
                  {r.status}
                </Tag>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default OrganizationDetail;
